import { isExternalLink } from './isExternalLink.ts';

// Adds `noopener`/`noreferrer` while preserving existing rel tokens (e.g. `nofollow`).
const addSecurityRelTokens = (link: HTMLAnchorElement): void => {
	const relTokens = new Set((link.getAttribute('rel') ?? '').split(/\s+/).filter(Boolean));
	relTokens.add('noopener');
	relTokens.add('noreferrer');
	link.setAttribute('rel', Array.from(relTokens).join(' '));
};

/**
 * Ensures every external `<a>` under `container` opens in a new tab with
 * `rel="noopener noreferrer"`. Internal links are untouched. An author-set
 * `target` is preserved, but a `target="_blank"` still gets the rel tokens to
 * prevent tab-napping.
 *
 * Used for server-rendered HTML injected via `dangerouslySetInnerHTML`, whose
 * links bypass the ADF renderer's link mark.
 */
export const ensureExternalLinksOpenInNewTab = (container: HTMLElement | null): void => {
	if (!container) {
		return;
	}

	const links = container.querySelectorAll<HTMLAnchorElement>('a[href]');
	links.forEach((link) => {
		// Use the reflected `href` property (absolute URL) so relative and
		// protocol-relative hrefs resolve consistently before classification.
		if (!isExternalLink(link.href)) {
			return;
		}

		const existingTarget = link.getAttribute('target');
		if (existingTarget === null) {
			// No author-set target: open in a new tab with security rel tokens.
			link.setAttribute('target', '_blank');
			addSecurityRelTokens(link);
		} else if (existingTarget === '_blank') {
			// Author already opens in a new tab: don't change the target, but
			// guarantee the rel tokens are present to prevent tab-napping.
			addSecurityRelTokens(link);
		}
		// Any other author-set target (e.g. _self, named frame) is left as-is.
	});
};
